// TopNewOperatorsWidget — sidebar widget showing 5 fastest-growing operators (last 7 days).
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp } from "lucide-react";
import { API, safeJson } from "../../lib/api";
import { useLanguage } from "../../i18n/LanguageContext";
import { UserAvatar } from "./UserAvatar";
import { FollowButton } from "./FollowButton";

const COPY = {
  title: {
    es: "TOP OPERADORES SEMANA",
    en: "TOP OPERATORS · WEEK",
    sk: "TOP OPERÁTORI · TÝŽDEŇ", de: "TOP TRADER DER WOCHE", "zh-CN": "本周顶级交易员",
  },
  subtitle: {
    es: "Más seguidores ganados en 7 días",
    en: "Most followers gained in 7 days",
    sk: "Najviac nových sledovateľov za 7 dní", de: "Meiste Follower in 7 Tagen gewonnen", "zh-CN": "7天内获得最多粉丝",
  },
  newFollowers: { es: "+nuevos", en: "+new", sk: "+noví", de: "+neue", "zh-CN": "+新增" },
  empty: {
    es: "Aún no hay actividad esta semana.",
    en: "No activity this week yet.",
    sk: "Tento týždeň ešte nie je aktivita.", de: "Diese Woche gibt es noch keine Aktivität.", "zh-CN": "本周暂无活动。",
  },
};
const t = (k, lang) => (COPY[k] && (COPY[k][lang] || COPY[k].es)) || k;

export const TopNewOperatorsWidget = () => {
  const { lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const tok = localStorage.getItem("auth_token") || localStorage.getItem("rebel_jwt");
        const r = await fetch(`${API}/shadow/social/top-new-operators?limit=5`, {
          credentials: "include",
          headers: tok ? { Authorization: `Bearer ${tok}` } : {},
        });
        const data = await safeJson(r);
        if (mounted && r.ok && data) setItems(data.items || []);
      } catch (_e) { /* non-critical */ }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <aside
      data-testid="top-new-operators"
      style={{
        background: "linear-gradient(135deg, rgba(245,166,35,0.04), rgba(0,212,255,0.03))",
        border: "1px solid rgba(245,166,35,0.18)",
        borderRadius: 12,
        padding: "14px 14px 10px",
      }}
    >
      <header className="mb-3 flex items-center gap-2">
        <TrendingUp className="h-3.5 w-3.5 text-amber-300" />
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-200">
            {t("title", lang)}
          </p>
          <p className="text-[9px] text-white/40 mt-0.5">{t("subtitle", lang)}</p>
        </div>
      </header>
      <ul className="space-y-2.5">
        {items.map((it, idx) => {
          const u = it.user;
          const handle = u.username || u.id;
          return (
            <li
              key={u.id}
              data-testid={`top-operator-${idx + 1}`}
              className="flex items-center gap-2.5"
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  color: idx === 0 ? "#f5a623" : "rgba(255,255,255,0.35)",
                  width: 12,
                  textAlign: "center",
                }}
              >
                {idx + 1}
              </span>
              <Link
                to={u.username ? `/shadow/perfil/${u.username}` : "#"}
                className="shrink-0"
              >
                <UserAvatar user={u} size="sm" />
              </Link>
              <div className="min-w-0 flex-1">
                <Link
                  to={u.username ? `/shadow/perfil/${u.username}` : "#"}
                  className="block truncate text-xs font-bold text-white hover:underline"
                >
                  {u.name}
                </Link>
                <div className="text-[10px] text-white/45">
                  <span className="text-amber-300 font-bold">+{it.gained_followers}</span>{" "}
                  {t("newFollowers", lang)}
                  {it.total_analyses > 0 && (
                    <span className="ml-1.5">· {it.total_analyses} an.</span>
                  )}
                </div>
              </div>
              <FollowButton
                targetUserId={u.id}
                initialFollowing={it.is_following}
                size="sm"
              />
            </li>
          );
        })}
      </ul>
    </aside>
  );
};

export default TopNewOperatorsWidget;
